<template>
  <main class="patient-profile">
    <div class="patient-profile__container">
      <button-back />
      <div class="patient-profile__head">
        <div class="patient-profile__head__row">
          <h1 class="patient-profile__head__title">
            Пациент:
            <span>
              {{ patient.name }}
              <span v-if="patient.age">
                ({{ patient.age }})
                <img
                  v-if="patient.birthDate && isBirthdayToday"
                  style="vertical-align:middle"
                  alt="День рождения"
                  src="@/assets/img/birthday.svg"
                  title="День рождения"
                />
              </span>
            </span>
          </h1>
          <slot name="head" />
        </div>
        <div class="patient-profile__head__info">
          <p class="patient-profile__head__add-date">
            Создан: {{ patient.addingDate }}
          </p>
          <p v-if="patient.archivedDate" class="patient-profile__head__archive-date">
            В архиве с {{ patient.archivedDate }}
          </p>
        </div>
      </div>
      <div class="patient-tabs tabs-fixed">
        <router-link
          v-for="item in tabs"
          v-bind:key="item.name"
          active-class="select"
          class="patient-tabs__item"
          v-bind:to="{ name: item.name }"
        >
          {{item.text}}
          <span
            v-if="isShowAlerts(item.text)"
            class="patient-tabs__item__mark-alert"
            v-bind:title="getAlertTitle(item.text)">
            !
          </span>
        </router-link>
      </div>
      <slot />
    </div>
  </main>
</template>

<script>
import { PATIENT_PROFILE } from '@/store/actions/patient';
import ButtonBack from '@/components/ButtonBack.vue';

const menuItems = [
  {
    text: 'Основное',
    name: 'ClinicPanelPatientBasic',
  },
  {
    text: 'Анамнез',
    name: 'ClinicPanelPatientAnamnesis',
  },
  {
    auth: 'manage-appointments',
    text: 'Анкета здоровья',
    name: 'ClinicPanelPatientHealthQuestionnaire',
  },
  {
    text: 'Визиты',
    name: 'ClinicPanelPatientVisits',
  },
  {
    text: 'Планы лечения',
    name: 'ClinicPanelPatientPlanList',
  },
  {
    text: 'История посещений',
    name: 'ClinicPanelPatientAppointments',
  },
  {
    text: 'Снимки',
    name: 'ClinicPanelPatientSnapshots',
  },
  {
    auth: 'patient-contacts',
    text: 'Документы',
    name: 'ClinicPanelPatientDocuments',
  },
  {
    auth: 'patient-invoices',
    text: 'Счета',
    name: 'ClinicPanelPatientInvoiceList',
  },
];

export default {
  components: { ButtonBack },
  computed: {
    patient: (vm) => vm.$store.getters.patient,
    patientId: ({ $route }) => parseInt($route.params.id, 10),
    tabs() {
      return this.$options.menuItems.filter(
        (candidate) => (
          candidate.auth
            ? this.$store.getters.isSectionAvailable(candidate.auth)
            : true
        ),
      );
    },
    alerts() {
      return [
        {
          name: 'Основное',
          has_errors: this.patient.hasBasicErrors,
          title: 'Заполните все недостающие данные',
        },
        {
          name: 'Анамнез',
          has_errors: this.patient.hasAnamnesisErrors,
          title: 'Заполните все недостающие данные',
        },
        {
          name: 'Документы',
          has_errors: this.patient.hasDocumentsErrors,
          title: 'Нет закрепленного договора',
        },
      ];
    },
    isBirthdayToday() {
      const birthDate = new Date(this.patient.birthDate);
      const today = new Date();
      return birthDate.getDate() === today.getDate()
        && birthDate.getMonth() === today.getMonth();
    },
  },
  created() {
    if (this.patient.id !== this.patientId) {
      const setPatient = (patient) => this.$store.commit(PATIENT_PROFILE, patient);
      this.fetchPatient(this.patientId).then((result) => result.forEach(setPatient));
    }
  },
  menuItems,
  methods: {
    /**
     * @description Получить пациента.
     * @param {number} id Идентификатор пациента.
     * @returns {Promise<Either>}
     */
    fetchPatient(id) {
      return this.$patientGateway.getPatientName(id);
    },
    /**
     * @description Отображать индикатор предупреждения.
     * @param name
     */
    isShowAlerts(name) {
      return this.alerts.find((element) => element.name === name && element.has_errors);
    },
    getAlertTitle(name) {
      return this.alerts.find((element) => element.name === name && element.has_errors)?.title;
    },
  },
  name: 'ClinicPanelPatientProfile',
};
</script>

<style lang="scss" scoped>

.patient {
  &__back-link {
    color: var(--action-color);
    display: flex;
    align-items: center;
    gap: 7px;
    width: fit-content;
    margin: 0 0 10px 0;
    &:before {
      content: '';
      background-image: url('@/assets/img/arrow/arrow-blue.svg');
      background-position: center;
      background-size: contain;
      width: 12px;
      height: 12px;
      display: block;
    }
    &:hover {
      text-decoration: underline;
    }
  }
  &-profile {
    flex-direction: row;
    display: flex;
    font-family: "Lato", sans-serif;
    flex: 1;
    overflow: hidden;
    &__container {
      width: 100%;
      padding: 38px 20px 48px 23px;
      overflow-y: auto;
    }
    &__head {
      width: 100%;
      &__info {
        display: flex;
        gap: 20px;
        margin: 10px 0;
      }
      &__title {
        min-height: 48px;
        font-size: 32px;
        line-height: 40px;
        font-weight: 600;
        color: #90a0b7;
        span {
          color: #192a3e;
        }
      }
      &__add-date {
        color: #707683;
        font-size: 14px;
        font-weight: 400;
        line-height: 17px;
      }
      &__archive-date {
        color: #cf0202;
        font-size: 14px;
        font-weight: 400;
        line-height: 17px;
      }
      &__row {
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;
      }
    }
  }
  &-tabs {
    width: 100%;
    flex-direction: row;
    display: flex;
    gap: 35px;
    border-bottom: 1px solid #c2cfe0;
    a {
      font-size: 18px;
      font-weight: 400;
      line-height: 22px;
    }
    &__item {
      cursor: pointer;
      color: #192a3e !important;
      font-size: 18px;
      font-weight: 400;
      line-height: 22px;
      padding-bottom: 9px;
      text-decoration: none;
      font-family: 'Lato', sans-serif;
      border-bottom: 2px solid transparent;
      &__mark-alert {
        font-size: 20px;
        font-weight: 600;
        color: #f21e1e;
      }
      &:hover {
        color: #0489e0 !important;
      }
      &.select {
        color: #0489e0 !important;
        transition: color var(--animate-duration);
        border-bottom: 2px solid #0489e0;
        font-size: 18px;
        font-weight: 400;
        line-height: 22px;
        font-family: 'Lato', sans-serif;
      }
    }
  }
}

@media screen and (max-width: 1530px) {
  .patient {
    &-tabs {
      flex-wrap: wrap;
      gap: 5px 20px;
      border-bottom: none;
      &__item {
        padding-bottom: 3px;
      }
    }
  }
}

@media screen and (max-width: 1310px) {
  .patient-profile__head {
    &__row {
      flex-direction: column-reverse;
      gap: 20px;
    }
  }
}

@media screen and (min-width: 960px){
  .tabs-fixed {
    background-color: white;
    position: sticky;
    z-index: 2;
    top: -40px;
    padding: 10px 0 0 0;
  }
}

@media screen and (max-width: 480px) {
  .patient {
    &-tabs {
        &__item {
          font-size: 16px;
          right: 30px;
        }
      }
  }
}
</style>
